import React, { useState } from "react";

// Define the StrategicVendor interface
interface StrategicVendor {
  id: string;
  name: string;
  inUse: boolean;
}

interface ConfigureVendorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  vendors: StrategicVendor[];
  onSave: (vendors: StrategicVendor[]) => void;
}

export const ConfigureVendorsModal: React.FC<ConfigureVendorsModalProps> = ({
  isOpen,
  onClose,
  vendors,
  onSave,
}) => {
  const [updatedVendors, setUpdatedVendors] =
    useState<StrategicVendor[]>(vendors);

    React.useEffect(() => {
          if (!isOpen) return;
              const handleEsc = (event: KeyboardEvent) => {
                  if (event.key === 'Escape') {
                      onClose();
                  }
              };
      
              window.addEventListener('keydown', handleEsc);
              return () => window.removeEventListener('keydown', handleEsc);
          });

  // Update state when vendors prop changes
  React.useEffect(() => {
    setUpdatedVendors(vendors);
  }, [vendors]);

  if (!isOpen) return null;

  const handleToggleVendor = (id: string) => {
    const newVendors = updatedVendors.map((vendor) => {
      if (vendor.id === id) {
        return { ...vendor, inUse: !vendor.inUse };
      }
      return vendor;
    });
    setUpdatedVendors(newVendors);
    onSave(newVendors);
  };

  return (
    <div className="fixed inset-0 overflow-hidden z-50">
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"></div>

      <div className="absolute inset-y-0 right-0 max-w-full flex">
        <div className="relative w-screen max-w-md">
          <div className="h-full flex flex-col bg-white shadow-xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between">
                <h2 className="text-xl font-medium text-[#002D72]">
                  Configure Strategic vendors
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <span className="sr-only">Close panel</span>
                  <svg
                    className="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-1 p-6">
              <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 rounded-md">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-[#F9FBFF]">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left font-bold text-[#002D72]"
                      >
                        Transformation Area
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left font-bold text-[#002D72]"
                      >
                        In Use ?
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {updatedVendors.map((vendor) => (
                      <tr key={vendor.id}>
                        <td className="px-6 py-4 font-medium italic">
                          {vendor.name}
                        </td>
                        <td
                          className={`px-6 py-4 ${
                            vendor.inUse ? "bg-[#F4FAEF]" : "bg-[#FAE7E8]"
                          }`}
                        >
                          <select
                            value={vendor.inUse ? "Yes" : "No"}
                            onChange={(e) => {
                              const selected = e.target.value === "Yes";
                              if (selected !== vendor.inUse) {
                                handleToggleVendor(vendor.id);
                              }
                            }}
                            className="bg-transparent  font-medium focus:outline-none"
                          >
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
